import axios, { CancelTokenSource } from 'axios';

import type { SalesforceService } from '@/services';
import type { PageToken } from '@/services/SalesforceService';
import type { SObject } from '@/types';

import type {
  CompareOverviewReaderIterator,
  CompareOverviewReader,
} from './interfaces';
import PageSeparator from './PageSeparator';
import CancelToken from './CancelToken';

export default class CompareOverviewReaderImpl
  implements CompareOverviewReader
{
  private readonly iterator: CompareOverviewReaderIterator;

  private cancelTokenSource: CancelTokenSource | null;

  private nextPageToken: PageToken | null;

  constructor(
    private readonly salesforceService: SalesforceService,
    private readonly sobject: SObject,
    private readonly options: {
      customValueFields: string[];
    }
  ) {
    this.nextPageToken = {};
    this.cancelTokenSource = null;
    const self = this;

    this.iterator = (async function* generator() {
      do {
        const { promise, cancelTokenSource } = self.salesforceService.compare(
          self.sobject,
          self.nextPageToken ?? undefined,
          {
            customValueFields: self.options.customValueFields,
          }
        );
        self.cancelTokenSource = cancelTokenSource;
        try {
          const { records, nextPageToken } = await promise;
          self.nextPageToken = nextPageToken ?? null;
          for (const record of records) {
            yield record;
          }
          yield new PageSeparator();
        } catch (e: unknown) {
          if (axios.isCancel(e)) {
            yield new CancelToken();
          } else {
            throw e;
          }
        }
      } while (self.nextPageToken !== null);
    })();
  }

  getIterator(): CompareOverviewReaderIterator {
    return this.iterator;
  }

  cancel(): void {
    if (this.cancelTokenSource === null) {
      return;
    }

    this.cancelTokenSource.cancel();
  }
}
